import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
  stripEmojis,
} from '@theme/utilities';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { buildSectionSelector, morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
  CartDrawerPromotionsLoadingEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { syncPersonalizationRecoverySnapshot } from '@theme/personalization-recovery';
import {
  updateLineItemPersonalizationEntry,
  SKIP_PERSONALIZATION_PROPERTY_KEY,
} from '@theme/line-item-personalization-cart';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const GIFT_MESSAGE_PROPERTY_KEY = 'Gift Message';
const GIFT_WRAP_ADDON_PROPERTY_KEY = '_gift_wrap_addon';
const GIFT_WRAP_GWP_SLOT_PROPERTY_KEY = '_gift_wrap_gwp_slot';
const PARENT_PID_PROPERTY_KEY = '_parent_pid';

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('input', this.#handleGiftMessageFieldInput);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('input', this.#handleGiftMessageFieldInput);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getCartItemRowByLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getCartItemRowByLine(line);
    const personalizationPid = cartItemRowToRemove?.dataset.personalizationPid;

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      personalizationPid,
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
        if (this.isDrawer) {
          this.#setDrawerEmptyState(true);
        }
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * @param {number} line - Shopify cart line index (1-based)
   * @returns {HTMLTableRowElement | undefined}
   */
  #getCartItemRowByLine(line) {
    const lineKey = String(line);
    return this.refs.cartItemRows.find((row) => row.dataset.cartLine === lineKey);
  }

  /**
   * @param {number} line - Shopify cart line index (1-based)
   * @returns {HTMLElement | undefined}
   */
  #getQuantitySelectorByLine(line) {
    const input = this.querySelector(`input[data-cart-line="${line}"]`);
    return input?.closest('cart-quantity-selector-component, quantity-selector-component') ?? undefined;
  }

  /**
   * @returns {{ sections: string, sections_url: string }}
   */
  #getSectionsPayload() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return {
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };
  }

  /**
   * @param {string} responseText
   * @param {number} [line]
   * @param {(error: unknown) => void} [onError]
   */
  #applyCartResponse(responseText, line, onError) {
    const parsedResponseText = JSON.parse(responseText);

    resetShimmer(this);

    if (parsedResponseText.errors) {
      if (onError) {
        onError(parsedResponseText.errors);
        return;
      }

      if (line) {
        this.#handleCartError(line, parsedResponseText);
      } else {
        console.error(parsedResponseText.errors);
      }
      return;
    }

    const sectionHtml = parsedResponseText.sections?.[this.sectionId];
    if (!sectionHtml) return;

    const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');

    // Grab the new cart item count from a hidden element
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    // Update data-cart-quantity for all matching variants
    this.#updateQuantitySelectors(parsedResponseText);

    this.#morphCartSection(sectionHtml);

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponseText, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );

    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * @param {string} url
   * @param {Record<string, unknown>} payload
   * @param {object} options
   * @param {string} options.action
   * @param {number} [options.line]
   * @param {(error: unknown) => void} [options.onError]
   */
  #updateCartLineItem(url, payload, { action, line, onError }) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);
    const { cartTotal } = this.refs;
    const personalizationPid = line && this.#getCartItemRowByLine(line)?.dataset.personalizationPid;

    this.#disableCartItems();
    cartTotal?.shimmer();

    fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }))
      .then(async (response) => {
        const responseText = await response.text();
        this.#applyCartResponse(responseText, line, onError);
        // Gift wrap/messages are deliberate edits, not passive cart refreshes.
        if (response.ok && !JSON.parse(responseText).errors && window.SDG?.Data?.enableCartAttributePersonalization && personalizationPid) {
          await syncPersonalizationRecoverySnapshot().catch(() => {
            console.warn('Unable to synchronize personalization recovery.');
          });
        }
      })
      .catch((error) => {
        onError?.(error);
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * @param {unknown} raw
   * @returns {Record<string, string>}
   */
  #parseLineItemProperties(raw) {
    if (typeof raw !== 'string' || !raw.trim()) return {};

    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};

      /** @type {Record<string, string>} */
      const properties = {};

      // Shopify serializes line item properties (item.properties | json) as an
      // array of [key, value] pairs, e.g. [["_pid","abc"],["Gift Message","Hi"]].
      // Support that shape as well as a plain object for safety. Preserving the
      // full set (including private `_`-prefixed keys) is required so editing
      // one property via /cart/change.js does not drop the others.
      const entries = Array.isArray(parsed) ? parsed : Object.entries(parsed);
      for (const entry of entries) {
        if (!Array.isArray(entry) || entry.length < 2) continue;
        const [key, value] = entry;
        if (typeof key !== 'string' || value == null) continue;
        properties[key] = String(value);
      }

      return properties;
    } catch {
      return {};
    }
  }

  /**
   * @param {number} line
   * @param {Event} event
   */
  onGiftWrapToggle(line, event) {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    const previousChecked = Boolean(input?.checked) === false;
    const row = this.#getCartItemRowByLine(line);
    const giftWrapLineKey = row?.dataset.giftWrapLineKey;

    if (!row) return;

    if (input?.checked) {
      if (giftWrapLineKey) return;

      const giftWrapVariantId = Number.parseInt(row.dataset.giftWrapVariantId || '', 10);
      const parentLineKey = row.dataset.key;
      const gwpSlot = row.dataset.gwpSlot || '';
      const quantity = Number.parseInt(row.dataset.lineItemQuantity || '', 10);
      const personalizationPid = row.dataset.personalizationPid || '';

      if (!Number.isFinite(giftWrapVariantId) || giftWrapVariantId <= 0 || !parentLineKey) {
        input.checked = false;
        return;
      }

      /** @type {Record<string, string>} */
      const properties = {
        [GIFT_WRAP_ADDON_PROPERTY_KEY]: 'true',
        [SKIP_PERSONALIZATION_PROPERTY_KEY]: 'false',
      };
      if (gwpSlot) {
        properties[GIFT_WRAP_GWP_SLOT_PROPERTY_KEY] = gwpSlot;
      }
      if (personalizationPid) {
        properties[PARENT_PID_PROPERTY_KEY] = personalizationPid;
      }

      /** @type {{ id: number, quantity: number, properties: Record<string, string>, parent_line_key?: string }} */
      const giftWrapItem = {
        id: giftWrapVariantId,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        properties,
      };

      giftWrapItem.parent_line_key = parentLineKey;

      this.#updateCartLineItem(
        Theme.routes.cart_add_url || '/cart/add.js',
        {
          items: [giftWrapItem],
          ...this.#getSectionsPayload(),
        },
        {
          action: 'add-gift-wrap',
          line,
          onError: () => {
            if (input) input.checked = previousChecked;
          },
        }
      );
      return;
    }

    if (!giftWrapLineKey) {
      return;
    }

    this.#updateCartLineItem(
      Theme.routes.cart_update_url || '/cart/update.js',
      {
        updates: {
          [giftWrapLineKey]: 0,
        },
        ...this.#getSectionsPayload(),
      },
      {
        action: 'remove-gift-wrap',
        line,
        onError: () => {
          if (input) input.checked = previousChecked;
        },
      }
    );
  }

  /**
   * @param {number} line
   * @param {Event} event
   */
  onGiftMessageToggle(line, event) {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    const previousChecked = Boolean(input?.checked) === false;
    if (input?.checked) return;

    const row = this.#getCartItemRowByLine(line);
    if (!row) {
      if (input) input.checked = true;
      return;
    }

    const quantity = Number.parseInt(row.dataset.lineItemQuantity || '', 10);
    const properties = this.#parseLineItemProperties(row.dataset.lineItemProperties);
    delete properties[GIFT_MESSAGE_PROPERTY_KEY];

    this.#updateCartLineItem(
      Theme.routes.cart_change_url,
      {
        line,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        properties,
        ...this.#getSectionsPayload(),
      },
      {
        action: 'remove-gift-message',
        line,
        onError: () => {
          if (input) input.checked = previousChecked;
        },
      }
    );
  }

  /**
   * Reveals the save button only when the gift message textarea differs from
   * the value currently stored on the line item.
   * @param {number} line
   * @param {Event} event
   */
  onGiftMessageInput(line, event) {
    const textarea = event.target instanceof HTMLTextAreaElement ? event.target : null;
    if (!textarea) return;

    // Strip before any row lookup so emojis never linger if the line can't be resolved.
    this.#sanitizeGiftMessageInput(textarea);

    const row = this.#getCartItemRowByLine(line);
    if (!row) return;

    const properties = this.#parseLineItemProperties(row.dataset.lineItemProperties);
    const original = (properties[GIFT_MESSAGE_PROPERTY_KEY] || '').trim();
    const saveButton = row.querySelector('.cart-items__gift-message-save');
    if (saveButton instanceof HTMLElement) {
      saveButton.hidden = textarea.value.trim() === original;
    }
  }

  /**
   * Live-strips emojis from cart gift message fields (typing and paste).
   * @param {Event} event
   */
  #handleGiftMessageFieldInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLTextAreaElement)) return;
    if (!target.classList.contains('cart-items__gift-message-input')) return;

    this.#sanitizeGiftMessageInput(target);
  };

  /**
   * Saves an edited gift message to the line item via the Cart change API,
   * reusing the same property-rewrite path as the remove handler. Clearing the
   * message and saving removes it.
   * @param {number} line
   */
  onGiftMessageSave(line) {
    const row = this.#getCartItemRowByLine(line);
    if (!row) return;

    const textarea = row.querySelector('.cart-items__gift-message-input');
    if (!(textarea instanceof HTMLTextAreaElement)) return;

    this.#sanitizeGiftMessageInput(textarea);

    const newValue = stripEmojis(textarea.value).trim();
    const quantity = Number.parseInt(row.dataset.lineItemQuantity || '', 10);
    const properties = this.#parseLineItemProperties(row.dataset.lineItemProperties);

    if (newValue === '') {
      delete properties[GIFT_MESSAGE_PROPERTY_KEY];
    } else {
      properties[GIFT_MESSAGE_PROPERTY_KEY] = newValue;
    }

    this.#updateCartLineItem(
      Theme.routes.cart_change_url,
      {
        line,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        properties,
        ...this.#getSectionsPayload(),
      },
      {
        action: 'edit-gift-message',
        line,
      }
    );
  }

  /**
   * Removes emojis from a gift message field and restores the caret when possible.
   * @param {HTMLTextAreaElement} textarea
   */
  #sanitizeGiftMessageInput(textarea) {
    const previousValue = textarea.value;
    const sanitizedValue = stripEmojis(previousValue);
    if (sanitizedValue === previousValue) return;

    const selectionStart = textarea.selectionStart ?? previousValue.length;
    const sanitizedBeforeCursor = stripEmojis(previousValue.slice(0, selectionStart));

    textarea.value = sanitizedValue;
    const nextCursor = Math.min(sanitizedBeforeCursor.length, sanitizedValue.length);
    textarea.setSelectionRange(nextCursor, nextCursor);
  }

  /**
   * @param {any} item
   * @returns {string}
   */
  #getNestedLineParentKey(item) {
    const relationship = item?.parent_relationship;
    if (!relationship || typeof relationship !== 'object') return '';
    if (typeof relationship.parent_key === 'string') return relationship.parent_key;
    const parent = relationship.parent;
    if (parent && typeof parent === 'object' && typeof parent.key === 'string') return parent.key;
    return '';
  }

  /**
   * @param {HTMLTableRowElement | undefined} row
   * @param {number} quantity
   * @returns {Record<string, number> | null}
   */
  #buildNestedQuantityUpdates(row, quantity) {
    if (!row) return null;

    const lineKey = row.dataset.key;
    if (!lineKey) return null;

    const isNestedLine = row.classList.contains('cart-items__nested-line');
    if (isNestedLine) return null;

    if (quantity === 0) {
      const giftWrapLineKey = row.dataset.giftWrapLineKey;

      if (!row.dataset.gwpSlot || !giftWrapLineKey || giftWrapLineKey === lineKey) {
        return null;
      }

      return {
        [lineKey]: 0,
        [giftWrapLineKey]: 0,
      };
    }

    /** @type {Set<string>} */
    const nestedKeys = new Set();

    (row.dataset.nestedLineKeys || '')
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean)
      .forEach((key) => nestedKeys.add(key));

    this.refs.cartItemRows
      .filter((nestedRow) => nestedRow.dataset.parentKey === lineKey)
      .forEach((nestedRow) => {
        const key = nestedRow.dataset.key;
        if (key) nestedKeys.add(key);
      });

    if (nestedKeys.size === 0) return null;

    /** @type {Record<string, number>} */
    const updates = { [lineKey]: quantity };

    nestedKeys.forEach((nestedKey) => {
      updates[nestedKey] = quantity;
    });

    return updates;
  }

  /**
   * @param {HTMLTableRowElement | undefined} row
   * @param {number} quantity
   * @returns {Promise<Record<string, number> | null>}
   */
  async #resolveNestedQuantityUpdates(row, quantity) {
    const updatesFromDom = this.#buildNestedQuantityUpdates(row, quantity);
    if (updatesFromDom) return updatesFromDom;

    const lineKey = row?.dataset.key;
    if (!row || !lineKey || quantity === 0 || row.classList.contains('cart-items__nested-line')) return null;

    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return null;

      const cart = await cartResponse.json();
      /** @type {any[]} */
      const cartItems = Array.isArray(cart?.items) ? cart.items : [];
      const nestedKeys = cartItems
        .filter((item) => this.#getNestedLineParentKey(item) === lineKey)
        .map((item) => item.key)
        .filter(Boolean);

      if (nestedKeys.length === 0) return null;

      /** @type {Record<string, number>} */
      const updates = { [lineKey]: quantity };
      nestedKeys.forEach((nestedKey) => {
        updates[nestedKey] = quantity;
      });

      return updates;
    } catch (error) {
      console.warn('Failed to resolve nested cart line keys for quantity sync:', error);
      return null;
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.personalizationPid] - The personalization id for the line.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const parentRow = this.#getCartItemRowByLine(line);

    cartTotal?.shimmer();

    this.#resolveNestedQuantityUpdates(parentRow, quantity)
      .then((nestedQuantityUpdates) => {
        const sectionsPayload = this.#getSectionsPayload();

        const body = JSON.stringify(
          nestedQuantityUpdates
            ? {
                updates: nestedQuantityUpdates,
                ...sectionsPayload,
              }
            : {
                line: line,
                quantity: quantity,
                ...sectionsPayload,
              }
        );

        const cartUpdateUrl = nestedQuantityUpdates
          ? Theme.routes.cart_update_url || '/cart/update.js'
          : Theme.routes.cart_change_url;

        return fetch(cartUpdateUrl, fetchConfig('json', { body }));
      })
      .then((response) => {
        if (!response) return null;
        return response.text();
      })
      .then(async (responseText) => {
        if (!responseText) return;

        this.#applyCartResponse(responseText, line);

        if (quantity === 0 && config.personalizationPid) {
          this.clearPersonalizationCartAttribute(config.personalizationPid);
        }
        if (window.SDG?.Data?.enableCartAttributePersonalization && config.personalizationPid) {
          await syncPersonalizationRecoverySnapshot().catch(() => {
            console.warn('Unable to synchronize personalization recovery.');
          });
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * @param {string} pid
   */
  clearPersonalizationCartAttribute(pid) {
    updateLineItemPersonalizationEntry({
      pid,
      payload: null,
    }).catch((error) => {
      console.error(error);
    });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * @param {string} message
   */
  #showDiscountCodeError(message) {
    requestAnimationFrame(() => {
      const component = document
        .getElementById(buildSectionSelector(this.sectionId))
        ?.querySelector('cart-discount-component');

      if (component && typeof component.showDiscountCodeError === 'function') {
        component.showDiscountCodeError(message);
      }
    });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getQuantitySelectorByLine(line);
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      const discountCodeError = event.detail?.discountCodeError;

      sectionRenderer
        .renderSection(this.sectionId, { cache: false })
        .then(() => {
          void window.GiftWithPurchase?.refreshOffersUI?.();

          if (typeof discountCodeError === 'string' && discountCodeError.length > 0) {
            this.#showDiscountCodeError(discountCodeError);
          }
        })
        .catch(() => {})
        .finally(() => {
          this.#clearPromotionsLoading();
        });
      return;
    }
    if (event.target === this) return;

    const isGwpCartUpdate = this.#isGwpCartUpdate(event);
    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];

    if (cartItemsHtml) {
      this.#morphCartSection(cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
      void window.GiftWithPurchase?.refreshOffersUI?.();

      if (isGwpCartUpdate) {
        this.#clearPromotionsLoading();
      }

      return;
    }

    sectionRenderer
      .renderSection(this.sectionId, { cache: false })
      .then(() => {
        if (isGwpCartUpdate) {
          void window.GiftWithPurchase?.refreshOffersUI?.();
        }
      })
      .catch(() => {})
      .finally(() => {
        if (isGwpCartUpdate) {
          this.#clearPromotionsLoading();
        }
      });
  };

  /**
   * @param {string} sectionHtml
   */
  #morphCartSection(sectionHtml) {
    if (!this.isDrawer) {
      morphSection(this.sectionId, sectionHtml, 'full');
      return;
    }

    const transition = this.#getDrawerCartTransition(sectionHtml);

    if (transition === 'empty-to-filled') {
      this.#morphDrawerToFilled(sectionHtml);
      return;
    }

    if (transition === 'filled-to-empty') {
      this.#morphDrawerToEmpty(sectionHtml);
      return;
    }

    morphSection(this.sectionId, sectionHtml, 'hydration');
  }

  /**
   * @param {string} sectionHtml
   * @returns {'empty-to-filled' | 'filled-to-empty' | 'update'}
   */
  #getDrawerCartTransition(sectionHtml) {
    const newSection = this.#parseSectionHtml(sectionHtml);
    const newDialog = newSection?.querySelector('.cart-drawer__dialog');
    const isNewCartEmpty = Boolean(newDialog?.classList.contains('cart-drawer--empty'));
    const isCurrentCartEmpty = this.#isDrawerEmpty();

    if (isCurrentCartEmpty && !isNewCartEmpty) return 'empty-to-filled';
    if (!isCurrentCartEmpty && isNewCartEmpty) return 'filled-to-empty';
    return 'update';
  }

  /**
   * @param {string} sectionHtml
   * @returns {HTMLElement | null}
   */
  #parseSectionHtml(sectionHtml) {
    const fragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
    return fragment.getElementById(buildSectionSelector(this.sectionId));
  }

  /**
   * Hydration morph only updates existing keyed targets, so the first add-to-cart
   * from an empty drawer must replace the drawer markup and clear the empty state.
   *
   * @param {string} sectionHtml
   */
  #morphDrawerToFilled(sectionHtml) {
    const newSection = this.#parseSectionHtml(sectionHtml);
    const newCartItems = newSection?.querySelector('cart-items-component[data-drawer]');

    if (!newCartItems) {
      morphSection(this.sectionId, sectionHtml, 'full');
      return;
    }

    morph(this, newCartItems, { ...MORPH_OPTIONS, hydrationMode: false });
    this.#setDrawerEmptyState(false, newSection);
    void window.GiftWithPurchase?.refreshOffersUI?.();
  }

  /**
   * @param {string} sectionHtml
   */
  #morphDrawerToEmpty(sectionHtml) {
    const newSection = this.#parseSectionHtml(sectionHtml);
    const newCartItems = newSection?.querySelector('cart-items-component[data-drawer]');

    if (newCartItems) {
      morph(this, newCartItems, { ...MORPH_OPTIONS, hydrationMode: false });
    }

    this.#setDrawerEmptyState(true, newSection);
  }

  /**
   * @param {boolean} isEmpty
   * @param {Element | null | undefined} [newSection]
   */
  #setDrawerEmptyState(isEmpty, newSection) {
    const cartDrawer = this.closest('cart-drawer-component');
    const currentDialog = cartDrawer?.querySelector('.cart-drawer__dialog');
    if (!(currentDialog instanceof HTMLDialogElement)) return;

    if (isEmpty) {
      currentDialog.classList.add('cart-drawer--empty');
      currentDialog.setAttribute('aria-labelledby', 'cart-drawer-heading-empty');
      currentDialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    currentDialog.classList.remove('cart-drawer--empty');

    const newDialog = newSection?.querySelector('.cart-drawer__dialog');
    const labelledBy = newDialog?.getAttribute('aria-labelledby');
    if (labelledBy) {
      currentDialog.setAttribute('aria-labelledby', labelledBy);
    }

    if (cartDrawer && 'updateSummaryStickyState' in cartDrawer && typeof cartDrawer.updateSummaryStickyState === 'function') {
      cartDrawer.updateSummaryStickyState();
    }
  }

  /**
   * @returns {boolean}
   */
  #isDrawerEmpty() {
    return !this.querySelector('[data-hydration-key="cart-drawer-summary"]');
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  #clearPromotionsLoading() {
    window.GiftWithPurchase?.setPromotionsLoading?.(false);

    if (!this.isDrawer) return;

    this.dispatchEvent(new CartDrawerPromotionsLoadingEvent(false, this.sectionId));
  }

  /**
   * @param {CartUpdateEvent | CartAddEvent | DiscountUpdateEvent} event
   * @returns {boolean}
   */
  #isGwpCartUpdate(event) {
    if (!(event instanceof CartUpdateEvent)) return false;

    const source = event.detail?.data?.source;
    return source === 'gift-with-purchase' || source === 'gwp-manual-add';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
